import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { Icon } from '../ui/shell';
import { themeVars, flowComponents } from './flowKit';
import useDemoActionGate from '../hooks/useDemoActionGate';
import DemoLockedDialog from '../ui/DemoLockedDialog';

/* ============================================================
   Onboarding — first-run flow, ported 1:1 from the reference
   flow.jsx. First-time only (gated by MyRoutes) + replayable
   from the Workflow page.
   Sign-in is handled by the real Login; this covers the in-panel
   first-run: Terms -> Meet Yaki -> Choose path -> branches.
   ============================================================ */

// Mock plans for the free-trial branch (real API wired later).
const PACKAGES = [
  { id: 'p1', name: 'Starter', price: 29, courses: 5, students: 100, assessments: 20 },
  { id: 'p2', name: 'Professional', price: 79, courses: 25, students: 500, assessments: 'Unlimited' },
  { id: 'p3', name: 'Enterprise', price: 199, courses: 'Unlimited', students: 'Unlimited', assessments: 'Unlimited' },
];

const OB_SPINE = [
  { id: 'intro', label: 'Meet Yaki', cap: 'A 90-second tour' },
  { id: 'options', label: 'Choose your path', cap: 'Tailor your setup' },
];

const OB_TIPS = {
  intro: "Here's the quick version of how I turn your course material into ready-to-run assessments.",
  options: "Almost there — tell me how you'd like to begin and I'll set the whole workspace up around you.",
};

const Wrap = styled.div`
  ${themeVars}
  ${flowComponents}

  &.ob { position: fixed; inset: 0; z-index: 50; display: grid; grid-template-columns: 0.92fr 1.08fr; background: var(--bg); color: var(--text); font-family: var(--font-body); }
  .ob__brand { position: relative; overflow: hidden; background: var(--rail-bg); color: #fff; display: flex; flex-direction: column; justify-content: space-between; padding: 44px clamp(28px, 4vw, 56px); }
  .ob__brand::before { content: ""; position: absolute; inset: 0; background: radial-gradient(120% 75% at 100% 0%, color-mix(in oklch, var(--accent) 60%, transparent), transparent 56%); }
  .ob__brand::after { content: ""; position: absolute; left: -10%; bottom: -20%; width: 70%; height: 55%; background: radial-gradient(circle at 30% 70%, color-mix(in oklch, var(--accent) 34%, transparent), transparent 62%); filter: blur(6px); pointer-events: none; }
  .ob__brandtop { position: relative; display: flex; align-items: center; justify-content: space-between; }
  .ob__brandmid, .ob__brandfoot { position: relative; }
  .ob__logo { display: flex; align-items: center; gap: 11px; }
  .ob__logo .rail__logo { width: 40px; height: 40px; font-size: 19px; }
  .ob__logo b { font-family: var(--font-head); font-weight: 760; font-size: 18px; letter-spacing: -.01em; }
  .ob__progresspill { font-size: 12.5px; font-weight: 700; white-space: nowrap; color: #fff; background: rgba(255,255,255,.12); border: 1px solid rgba(255,255,255,.14); padding: 6px 12px; border-radius: 999px; }
  .ob__progresspill span { color: rgba(255,255,255,.55); font-weight: 600; }

  .ob__kicker { display: inline-flex; align-items: center; gap: 7px; font-size: 11.5px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: color-mix(in oklch, var(--accent) 55%, #fff); background: rgba(255,255,255,.07); padding: 5px 11px; border-radius: 999px; margin-bottom: 18px; }
  .ob__brandmid h2 { font-family: var(--font-head); font-weight: 770; font-size: clamp(26px, 2.9vw, 38px); line-height: 1.1; letter-spacing: -.025em; margin: 0 0 14px; }
  .ob__brandmid p { margin: 0; font-size: 15px; line-height: 1.6; color: rgba(255,255,255,.7); max-width: 40ch; }

  .ob__steps { position: relative; display: flex; flex-direction: column; gap: 4px; margin-top: 30px; }
  .ob__steps::before { content: ""; position: absolute; left: 28px; top: 30px; bottom: 30px; width: 2px; background: rgba(255,255,255,.13); border-radius: 2px; }
  .ob__steps-fill { position: absolute; left: 28px; top: 30px; width: 2px; background: var(--accent); border-radius: 2px; transition: height .35s cubic-bezier(.3,.7,.2,1); }
  .ob__step { position: relative; display: flex; align-items: center; gap: 14px; padding: 9px 12px; border-radius: 13px; transition: .22s; }
  .ob__step.is-now { background: rgba(255,255,255,.09); box-shadow: inset 0 0 0 1px rgba(255,255,255,.08); }
  .ob__step-dot { position: relative; z-index: 1; width: 32px; height: 32px; border-radius: 50%; display: grid; place-items: center; flex: none; font-size: 13px; font-weight: 700; background: var(--rail-bg); color: rgba(255,255,255,.55); box-shadow: inset 0 0 0 2px rgba(255,255,255,.18); transition: .22s; }
  .ob__step.is-done .ob__step-dot { background: var(--accent); color: #fff; box-shadow: none; }
  .ob__step.is-now .ob__step-dot { background: #fff; color: var(--rail-bg); box-shadow: 0 0 0 4px color-mix(in oklch, var(--accent) 40%, transparent); }
  .ob__step-meta { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
  .ob__step-label { font-size: 14px; font-weight: 650; white-space: nowrap; color: rgba(255,255,255,.6); transition: .22s; }
  .ob__step.is-done .ob__step-label, .ob__step.is-now .ob__step-label { color: #fff; }
  .ob__step-cap { font-size: 11.5px; white-space: nowrap; color: rgba(255,255,255,.42); font-weight: 500; }
  .ob__step.is-now .ob__step-cap { color: rgba(255,255,255,.6); }

  .ob__brandfoot { display: flex; flex-direction: column; gap: 22px; }
  .ob__tip { display: flex; gap: 12px; align-items: flex-start; background: rgba(255,255,255,.06); border: 1px solid rgba(255,255,255,.1); border-radius: 15px; padding: 14px 15px; }
  .ob__tip-av { width: 34px; height: 34px; border-radius: 10px; flex: none; display: grid; place-items: center; font-family: var(--font-head); font-weight: 760; font-size: 15px; color: #fff; background: linear-gradient(140deg, var(--accent), color-mix(in oklch, var(--accent) 55%, #000)); box-shadow: 0 4px 12px -4px color-mix(in oklch, var(--accent) 70%, transparent); }
  .ob__tip-name { font-size: 11.5px; font-weight: 700; color: color-mix(in oklch, var(--accent) 50%, #fff); letter-spacing: .02em; }
  .ob__tip-body p { margin: 2px 0 0; font-size: 13px; line-height: 1.5; color: rgba(255,255,255,.82); }
  .ob__brandstats { display: flex; gap: 26px; }
  .ob__brandstats div b { font-family: var(--font-head); font-weight: 760; font-size: 23px; display: block; }
  .ob__brandstats div span { font-size: 12px; color: rgba(255,255,255,.6); }

  .ob__panel { display: flex; flex-direction: column; overflow-y: auto; padding: clamp(28px, 4vw, 58px) clamp(26px, 5vw, 88px); }
  .ob__panel-inner { margin: auto 0; width: 100%; max-width: 480px; }
  .ob__eyebrow { display: inline-flex; align-items: center; gap: 7px; font-size: 12px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; color: var(--accent-strong); margin-bottom: 12px; white-space: nowrap; }
  .ob__h { font-family: var(--font-head); font-size: clamp(24px, 2.4vw, 30px); font-weight: 760; letter-spacing: -.02em; margin: 0 0 6px; }
  .ob__sub { margin: 0 0 26px; font-size: 15px; color: var(--text-muted); line-height: 1.55; }
  .ob__actions { display: flex; align-items: center; gap: 12px; margin-top: 26px; }
  .ob__actions .btn { height: 46px; }
  .ob__back { display: flex; width: max-content; align-items: center; gap: 6px; font-size: 13.5px; font-weight: 650; color: var(--text-muted); background: none; border: none; cursor: pointer; padding: 6px 2px; margin-bottom: 6px; }
  .ob__back:hover { color: var(--text); }

  .ob__choices { display: grid; gap: 12px; }
  .choice { display: flex; gap: 14px; align-items: flex-start; text-align: left; padding: 17px 18px; border-radius: var(--r-lg); border: 1.5px solid var(--border); background: var(--surface); cursor: pointer; transition: .16s; width: 100%; }
  .choice:hover { border-color: var(--accent); background: var(--accent-soft); transform: translateY(-1px); box-shadow: var(--shadow-md); }
  .choice.is-sel { border-color: var(--accent); box-shadow: 0 0 0 3px var(--accent-soft); }
  .choice__ico { width: 42px; height: 42px; border-radius: 12px; flex: none; display: grid; place-items: center; background: var(--accent-soft); color: var(--accent-strong); box-shadow: inset 0 0 0 1px color-mix(in oklch, var(--accent) 22%, transparent); }
  .choice__txt h4 { margin: 0 0 3px; font-size: 15px; font-weight: 700; }
  .choice__txt p { margin: 0; font-size: 13px; color: var(--text-muted); line-height: 1.5; }
  .choice__tag { margin-left: auto; }

  .ob__terms { border: 1px solid var(--border); border-radius: var(--r-md); background: var(--surface-2); padding: 18px 20px; max-height: 230px; overflow-y: auto; font-size: 13.5px; line-height: 1.65; color: var(--text-muted); }
  .ob__terms h5 { margin: 14px 0 5px; color: var(--text); font-size: 13.5px; font-weight: 700; }
  .ob__terms h5:first-child { margin-top: 0; }
  .ob__check { display: flex; align-items: flex-start; gap: 10px; margin-top: 16px; font-size: 14px; font-weight: 600; cursor: pointer; line-height: 1.45; }
  .ob__check input { width: 18px; height: 18px; margin-top: 1px; accent-color: var(--accent); flex: none; }

  .ob__video { position: relative; aspect-ratio: 16/9; border-radius: var(--r-lg); overflow: hidden; background: linear-gradient(135deg, color-mix(in oklch, var(--accent) 38%, var(--rail-bg)), var(--rail-bg)); display: grid; place-items: center; box-shadow: var(--shadow-lg); }
  .ob__video::after { content: ""; position: absolute; inset: 0; background: radial-gradient(80% 60% at 50% 40%, rgba(255,255,255,.16), transparent 70%); }
  .ob__play { position: relative; z-index: 1; width: 72px; height: 72px; border-radius: 50%; background: rgba(255,255,255,.92); color: var(--rail-bg); display: grid; place-items: center; border: none; cursor: pointer; box-shadow: 0 12px 30px -8px rgba(0,0,0,.5); transition: .16s; }
  .ob__play:hover { transform: scale(1.06); }
  .ob__videocap { position: absolute; left: 18px; bottom: 16px; z-index: 1; color: #fff; display: flex; flex-direction: column; gap: 2px; }
  .ob__videocap b { font-family: var(--font-head); font-size: 16px; font-weight: 700; white-space: nowrap; }
  .ob__videocap span { font-size: 12.5px; color: rgba(255,255,255,.72); }

  .ob__list { display: grid; gap: 10px; margin: 4px 0 0; }
  .ob__li { display: flex; gap: 11px; align-items: flex-start; font-size: 14px; line-height: 1.5; }
  .ob__li-ico { width: 26px; height: 26px; border-radius: 8px; flex: none; display: grid; place-items: center; background: color-mix(in oklch, var(--success) 15%, var(--surface)); color: var(--success); }
  .ob__li b { font-weight: 650; }
  .ob__li span { color: var(--text-muted); }

  .ob__panelcard { border: 1px solid var(--border); border-radius: var(--r-lg); background: var(--surface); padding: 20px; box-shadow: var(--shadow-sm); }
  .ob__cred { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 11px 14px; border-radius: var(--r-md); background: var(--surface-2); border: 1px dashed var(--border-strong); font-family: var(--font-head); }
  .ob__cred span { font-size: 12px; color: var(--text-subtle); font-family: var(--font-body); font-weight: 600; }
  .ob__cred b { font-size: 14px; font-weight: 700; letter-spacing: .01em; }

  .ob__pkgs { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
  .pkg { position: relative; border: 1.5px solid var(--border); border-radius: var(--r-lg); background: var(--surface); padding: 18px 16px; cursor: pointer; transition: .16s; }
  .pkg:hover { border-color: var(--accent); transform: translateY(-2px); box-shadow: var(--shadow-md); }
  .pkg.is-sel { border-color: var(--accent); box-shadow: 0 0 0 3px var(--accent-soft); }
  .pkg__pop { position: absolute; top: -10px; right: 12px; }
  .pkg__name { font-size: 13px; font-weight: 700; color: var(--text-muted); }
  .pkg__price { font-family: var(--font-head); font-weight: 780; font-size: 27px; margin: 6px 0 2px; }
  .pkg__price small { font-size: 13px; font-weight: 600; color: var(--text-subtle); }
  .pkg__feat { list-style: none; margin: 12px 0 0; padding: 0; display: grid; gap: 7px; font-size: 12.5px; color: var(--text-muted); }
  .pkg__feat li { display: flex; gap: 7px; align-items: center; }
  .pkg__feat svg { color: var(--success); flex: none; }

  .ob__stripe { display: grid; place-items: center; gap: 18px; padding: 30px 0; text-align: center; }
  .ob__spinner { width: 46px; height: 46px; border-radius: 50%; border: 4px solid var(--surface-3); border-top-color: var(--accent); animation: ob-spin .8s linear infinite; }
  @keyframes ob-spin { to { transform: rotate(360deg); } }

  @media (max-width: 920px) {
    &.ob { grid-template-columns: 1fr; }
    .ob__brand { display: none; }
    .ob__choices--2, .ob__pkgs { grid-template-columns: 1fr; }
  }
`;

function ObBrand({ step }) {
  const idx = OB_SPINE.findIndex((s) => s.id === step);
  const eff = idx === -1 ? OB_SPINE.length - 1 : idx;
  const frac = OB_SPINE.length > 1 ? eff / (OB_SPINE.length - 1) : 0;
  const tip = OB_TIPS[step] || OB_TIPS[OB_SPINE[eff].id];
  return (
    <div className="ob__brand">
      <div className="ob__brandtop">
        <div className="ob__logo">
          <div className="rail__logo">S</div>
          <b>StudyAK</b>
        </div>
        <span className="ob__progresspill">Step {eff + 1} <span>of {OB_SPINE.length}</span></span>
      </div>

      <div className="ob__brandmid">
        <div className="ob__kicker"><Icon name="sparkles" size={13} />Workspace setup</div>
        <h2>Set up your AI<br />teaching workspace</h2>
        <p>A few quick steps and Yaki has your courses, assessments and learners ready to go.</p>

        <div className="ob__steps">
          <span className="ob__steps-fill" style={{ height: `calc((100% - 60px) * ${frac})` }} />
          {OB_SPINE.map((s, i) => (
            <div key={s.id} className={'ob__step' + (i < eff ? ' is-done' : i === eff ? ' is-now' : '')}>
              <span className="ob__step-dot">{i < eff ? <Icon name="check" size={14} /> : i + 1}</span>
              <span className="ob__step-meta">
                <span className="ob__step-label">{s.label}</span>
                <span className="ob__step-cap">{s.cap}</span>
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="ob__brandfoot">
        <div className="ob__tip">
          <div className="ob__tip-av">Y</div>
          <div className="ob__tip-body">
            <span className="ob__tip-name">Yaki</span>
            <p>{tip}</p>
          </div>
        </div>
        <div className="ob__brandstats">
          {[['12k+', 'instructors'], ['3 min', 'to first course'], ['91%', 'pass rate']].map(([n, l]) => (
            <div key={l}><b className="tnum">{n}</b><span>{l}</span></div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ObPanel({ eyebrow, icon, title, sub, children, back }) {
  return (
    <div className="ob__panel">
      <div className="ob__panel-inner">
        {back && <button className="ob__back" onClick={back}><Icon name="chevR" size={15} style={{ transform: 'rotate(180deg)' }} />Back</button>}
        {eyebrow && <div className="ob__eyebrow"><Icon name={icon || 'sparkles'} size={14} />{eyebrow}</div>}
        <h1 className="ob__h">{title}</h1>
        {sub && <p className="ob__sub">{sub}</p>}
        {children}
      </div>
    </div>
  );
}

const PrimaryBtn = ({ children, onClick }) => (
  <button className="btn btn--primary" style={{ flex: 1 }} onClick={onClick}>{children}<Icon name="chevR" size={16} /></button>
);

export default function Onboarding({ onEnterApp, initialStep }) {
  const { guardAction, lockedDialogOpen, setLockedDialogOpen } = useDemoActionGate();
  const [step, setStep] = useState(initialStep || 'intro');
  const [pkg, setPkg] = useState('p2');
  const go = (s) => setStep(s);

  const screen = () => {
    switch (step) {
      case 'intro':
        return (
          <ObPanel eyebrow="Step 1 · Welcome" icon="play" title="Meet Yaki, your teaching assistant"
            sub="90 seconds on how StudyAK builds courses and assessments from your material.">
            <div className="ob__video">
              <button className="ob__play"><Icon name="play" size={28} /></button>
              <div className="ob__videocap"><b>Welcome to StudyAK</b><span>1:28 · Product tour</span></div>
            </div>
            <div className="ob__actions">
              <button className="btn btn--ghost" onClick={() => go('options')}>Skip</button>
              <PrimaryBtn onClick={() => go('options')}>Continue</PrimaryBtn>
            </div>
          </ObPanel>
        );
      case 'options':
        return (
          <ObPanel eyebrow="Step 2 · Choose your path" icon="route" title="How would you like to start?"
            sub="Pick a path — you can switch any time from your dashboard." back={() => go('intro')}>
            <div className="ob__choices">
              {[
                ['instructor', 'users', 'Explore as an instructor', 'Open a fully-loaded demo workspace and see Yaki in action.', 'Recommended', 'tag--accent'],
                ['trial', 'rocket', 'Start a free trial', 'Pick a plan and build your own course with your material.', '14 days free', 'tag--green'],
                ['learner', 'eye', 'Explore as a learner', 'See the student side — take a sample course and assessment.', null, null],
                ['contact', 'mail', 'Contact a representative', 'Talk to our team about rollout for your whole organisation.', null, null],
              ].map(([id, ic, h, p, tg, tc]) => (
                <button key={id} className="choice" onClick={() => go(id)}>
                  <div className="choice__ico"><Icon name={ic} size={20} /></div>
                  <div className="choice__txt"><h4>{h}</h4><p>{p}</p></div>
                  {tg && <span className={'tag ' + tc + ' choice__tag'}>{tg}</span>}
                </button>
              ))}
            </div>
          </ObPanel>
        );
      case 'learner':
        return (
          <ObPanel eyebrow="Learner preview" icon="eye" title="Your learner assistant is ready"
            sub="Download the StudyAK learner app or open it in the browser. Use the sample credentials below to sign in."
            back={() => go('options')}>
            <div className="ob__panelcard">
              <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
                <button className="btn btn--primary" style={{ flex: 1 }}><Icon name="download" size={16} />Download app</button>
                <button className="btn btn--ghost" style={{ flex: 1 }}><Icon name="globe" size={16} />Open in browser</button>
              </div>
              <div className="ob__cred" style={{ marginBottom: 8 }}><div><span>Username</span><br /><b>learner@studyak.demo</b></div><Icon name="copy" size={16} style={{ color: 'var(--text-subtle)' }} /></div>
              <div className="ob__cred"><div><span>Password</span><br /><b>sample-2026</b></div><Icon name="copy" size={16} style={{ color: 'var(--text-subtle)' }} /></div>
            </div>
            <div className="ob__actions">
              <button className="btn btn--ghost" onClick={() => go('options')}>Back to options</button>
              <PrimaryBtn onClick={() => go('instructor')}>Switch to instructor view</PrimaryBtn>
            </div>
          </ObPanel>
        );
      case 'contact':
        return (
          <ObPanel eyebrow="Talk to us" icon="mail" title="Contact a representative"
            sub="Tell us about your organisation and we'll put together a rollout plan." back={() => go('options')}>
            <div className="form-grid">
              <div className="field"><label>Organisation</label><input className="input" defaultValue="Riverside Health Academy" /></div>
              <div className="field"><label>Team size</label><select className="select" defaultValue="100–500 learners"><option>1–25 learners</option><option>26–100 learners</option><option>100–500 learners</option><option>500+ learners</option></select></div>
            </div>
            <div className="field"><label>What would you like to achieve?</label><textarea className="textarea" defaultValue="We'd like to roll StudyAK out across our clinical training programmes." /></div>
            <div className="ob__actions">
              <button className="btn btn--ghost" onClick={() => go('options')}>Back</button>
              <PrimaryBtn onClick={() => go('contact_done')}>Send request</PrimaryBtn>
            </div>
          </ObPanel>
        );
      case 'contact_done':
        return (
          <ObPanel eyebrow="Request sent" icon="check" title="We'll be in touch within one business day"
            sub="A StudyAK specialist will email you to set up your rollout call. In the meantime, feel free to explore the demo.">
            <div className="ob__actions">
              <button className="btn btn--ghost" onClick={() => go('options')}>Back to options</button>
              <PrimaryBtn onClick={() => go('instructor')}>Explore the demo</PrimaryBtn>
            </div>
          </ObPanel>
        );
      case 'trial':
      case 'packages':
        return (
          <ObPanel eyebrow="Free trial" icon="rocket" title="Choose your plan"
            sub="Start with a 14-day free trial. You won't be charged until the trial ends — cancel anytime."
            back={() => go('options')}>
            <div className="ob__pkgs">
              {PACKAGES.map((p) => (
                <div key={p.id} className={'pkg' + (pkg === p.id ? ' is-sel' : '')} onClick={() => {
                  if (!guardAction('action.onboarding.selectPackage')) return;
                  setPkg(p.id);
                }}>
                  {p.name === 'Professional' && <span className="tag tag--accent pkg__pop">Popular</span>}
                  <div className="pkg__name">{p.name}</div>
                  <div className="pkg__price">${p.price}<small>/mo</small></div>
                  <ul className="pkg__feat">
                    <li><Icon name="check" size={13} />{p.courses} courses</li>
                    <li><Icon name="check" size={13} />{p.students} learners</li>
                    <li><Icon name="check" size={13} />{p.assessments} assessments</li>
                  </ul>
                </div>
              ))}
            </div>
            <div className="ob__actions"><PrimaryBtn onClick={() => {
              if (!guardAction('action.onboarding.selectPackage')) return;
              go('cc');
            }}>Continue with {PACKAGES.find((p) => p.id === pkg).name}</PrimaryBtn></div>
          </ObPanel>
        );
      case 'cc':
        return (
          <ObPanel eyebrow="Free trial" icon="card" title="Add a card to start your trial"
            sub="Required to activate your trial — you won't be charged until your 14 days are up." back={() => go('packages')}>
            <div className="field"><label>Cardholder name</label><input className="input" defaultValue="Maya Reardon" /></div>
            <div className="field"><label>Card number</label><input className="input" defaultValue="4242 4242 4242 4242" /></div>
            <div className="form-grid">
              <div className="field"><label>Expiry</label><input className="input" defaultValue="08 / 28" /></div>
              <div className="field"><label>CVC</label><input className="input" defaultValue="123" /></div>
            </div>
            <div className="actas" style={{ margin: '4px 0 0' }}><Icon name="lock" size={16} /><span>Secured by <b>Stripe</b> — StudyAK never stores your card details.</span></div>
            <div className="ob__actions"><PrimaryBtn onClick={() => {
              if (!guardAction('action.onboarding.startTrial')) return;
              go('stripe');
              setTimeout(() => setStep((s) => (s === 'stripe' ? 'ccdone' : s)), 2200);
            }}>Start trial</PrimaryBtn></div>
          </ObPanel>
        );
      case 'stripe':
        return (
          <ObPanel title="Redirecting to Stripe…" sub="Confirming your payment method securely.">
            <div className="ob__stripe">
              <div className="ob__spinner" />
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)', fontWeight: 600, fontSize: 14 }}><Icon name="lock" size={16} />Verifying card with Stripe</div>
            </div>
          </ObPanel>
        );
      case 'ccdone':
        return (
          <ObPanel eyebrow="You're in" icon="check" title="Trial activated 🎉"
            sub="Your card is saved and your 14-day Professional trial has started. Let's build your first course.">
            <div className="ob__list">
              {[['Payment method confirmed', '— billing starts in 14 days'], ['Professional plan unlocked', '— 25 courses, 500 learners'], ['Workspace ready', '— Yaki is standing by']].map(([h, t], i) => (
                <div className="ob__li" key={i}><span className="ob__li-ico"><Icon name="check" size={15} /></span><span><b>{h}</b> {t}</span></div>
              ))}
            </div>
            <div className="ob__actions"><PrimaryBtn onClick={() => onEnterApp('dashboard', { wizard: true })}>Go to dashboard</PrimaryBtn></div>
          </ObPanel>
        );
      case 'instructor':
      case 'demo':
        return (
          <ObPanel eyebrow="Instructor demo" icon="users" title="Your demo workspace is ready"
            sub="We've loaded your academy with courses, sample learners and live assessment data so you can explore everything Yaki does."
            back={() => go('options')}>
            <div className="ob__panelcard">
              <div className="ob__list">
                {[['course', '6 courses', 'Pharmacology, Anatomy, Compliance & more'], ['users', '274 sample learners', 'with realistic progress & scores'], ['assess', '6 live assessments', 'graded and ready to analyse']].map(([ic, h, p]) => (
                  <div className="ob__li" key={h}><span className="ob__li-ico" style={{ background: 'var(--accent-soft)', color: 'var(--accent-strong)' }}><Icon name={ic} size={15} /></span><span><b>{h}</b> — {p}</span></div>
                ))}
              </div>
            </div>
            <div className="ob__actions"><PrimaryBtn onClick={() => go('sample')}>Open demo workspace</PrimaryBtn></div>
          </ObPanel>
        );
      case 'sample':
        return (
          <ObPanel eyebrow="Yaki" icon="sparkles" title="Where would you like to start?"
            sub="Yaki opened your workspace with a few sample tasks. Pick one to see how it works — or jump straight to the dashboard."
            back={() => go('demo')}>
            <div className="ob__choices">
              {[
                ['assess', 'Analyse baseline assessment results', 'See where the cohort started and what to teach first.', 'dashboard'],
                ['users', 'Identify learners who need support', 'Yaki flags at-risk learners automatically.', 'scores'],
                ['course', 'Analyse a course', 'Review engagement and question performance.', 'courses'],
                ['eye', 'Analyse a learner', "Drill into one learner's full progress.", 'scores'],
                ['cog', 'Customise your account', 'Branding, domains and team settings.', 'settings'],
              ].map(([ic, h, p, route]) => (
                <button key={h} className="choice" onClick={() => onEnterApp(route)}>
                  <div className="choice__ico"><Icon name={ic} size={20} /></div>
                  <div className="choice__txt"><h4>{h}</h4><p>{p}</p></div>
                  <Icon name="chevR" size={18} style={{ marginLeft: 'auto', alignSelf: 'center', color: 'var(--text-subtle)' }} />
                </button>
              ))}
            </div>
            <div className="ob__actions"><PrimaryBtn onClick={() => onEnterApp('dashboard')}>Skip — go to dashboard</PrimaryBtn></div>
          </ObPanel>
        );
      default:
        return null;
    }
  };

  return (
    <>
    <Wrap className="ob">
      <ObBrand step={step} />
      {screen()}
    </Wrap>
    <DemoLockedDialog open={lockedDialogOpen} onClose={() => setLockedDialogOpen(false)} />
    </>
  );
}
